import { useEffect, useState } from "react";
import { useHyper, useWidgets } from "@juspay-tech/react-hyper-js";
import { BasePaymentProviderReturn, PaymentProvider } from "./usePayment";
import {
  extractErrorMessage,
  extractUserFriendlyErrorMessage,
  UserFacingError,
} from "../util/error";
import { useHyperPaymentContext } from "./HyperPaymentContext";
import errorNotifier from "../../../shared/modules/error-notifier";
import { getTranslation } from "../../../shared/modules/config";

export interface HyperPaymentResult {
  paymentId: string;
}

const validErrorTypes = [
  "card_error",
  "validation_error",
  "payment_methods_empty",
];

const useHyperPayment = ({
  paymentNarvarRefId,
}: {
  paymentNarvarRefId?: string;
}): BasePaymentProviderReturn => {
  const [capturingPayment, setCapturingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string>();
  const { savedHyper, savedElements } = useHyperPaymentContext();

  const isPaymentConfigurationError = !paymentNarvarRefId;

  const makePayment = async (): Promise<HyperPaymentResult> => {
    try {
      setCapturingPayment(true);
      setPaymentError("");

      if (!paymentNarvarRefId || !savedHyper || !savedElements) {
        console.error(
          "Hyper switch payment narvar ref id not available",
          paymentNarvarRefId,
        );
        errorNotifier.critical("Hyper switch payment configuration error", {
          paymentNarvarRefId,
          savedHyper: !!savedHyper,
          savedElements: !!savedElements,
        });
        throw new UserFacingError(
          "Payment was not configured correctly. Please contact support.",
        );
      }
      const response = await savedHyper.confirmPayment({
        elements: savedElements,
        confirmParams: {
          return_url: `${window.location.origin}/completion`,
        },
      });
      const { error, status, error_message } = response;

      if (error) {
        if (validErrorTypes.includes(error.type)) {
          throw new UserFacingError(error.message);
        } else {
          errorNotifier.critical(
            `Unexpected HyperSwitch payment error: ${extractErrorMessage(error)}`,
            {
              details: error,
              status,
              response,
            },
          );
          throw new UserFacingError();
        }
      }
      if (error_message) {
        throw new UserFacingError(error_message);
      }
      if (status === "requires_capture" || status === "succeeded") {
        return { paymentId: paymentNarvarRefId };
      }

      errorNotifier.critical(
        `Unexpected HyperSwitch payment status: ${status}`,
        {
          details: status,
          response,
        },
      );
      throw new UserFacingError(
        "Payment was not captured correctly. Please try again.",
      );
    } catch (error: unknown) {
      setPaymentError(
        extractUserFriendlyErrorMessage(
          error,
          getTranslation("shop_now_checkout_payment_error_default"),
        ),
      );
      if (!(error instanceof UserFacingError)) {
        errorNotifier.critical("Hyper switch payment error", {
          details: error,
        });
      }
      throw error;
    } finally {
      setCapturingPayment(false);
    }
  };

  return {
    capturingPayment,
    succeededPaymentIntentId: "",
    makePayment,
    paymentError,
    isPaymentConfigurationError,
    eligiblePaymentProvider: PaymentProvider.NARVAR,
  };
};

export const useSaveHyperElements = () => {
  const hyper = useHyper();
  const elements = useWidgets();
  const { setSavedHyper, setSavedElements } = useHyperPaymentContext();
  useEffect(() => {
    if (hyper) {
      setSavedHyper(hyper);
    }
    if (elements) {
      setSavedElements(elements);
    }
  }, [hyper, elements]);

  return { hyper, elements };
};

export default useHyperPayment;
